import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { INFRA_MAP, WORKER_URLS } from "@theg1group/config";
import { useCallback, useEffect, useRef, useState } from "react";
function resolveWorkers(entry, currentPath) {
    const workers = [...entry.workers];
    if (currentPath && entry.routeWorkers) {
        for (const [pattern, extra] of Object.entries(entry.routeWorkers)) {
            if (currentPath.startsWith(pattern)) {
                for (const w of extra) {
                    if (!workers.includes(w))
                        workers.push(w);
                }
            }
        }
    }
    return workers;
}
function HealthDot({ status }) {
    const color = status === "ok"
        ? "bg-success"
        : status === "error"
            ? "bg-red-500"
            : status === "checking"
                ? "bg-yellow-400 animate-pulse"
                : "bg-gray-300";
    return _jsx("span", { className: `inline-block h-2 w-2 rounded-full ${color}` });
}
export function DevInfoOverlay({ isAdmin, appId, currentPath, enableHealthCheck = false, }) {
    const [open, setOpen] = useState(false);
    const [health, setHealth] = useState({});
    const panelRef = useRef(null);
    const entry = INFRA_MAP[appId];
    const workers = entry ? resolveWorkers(entry, currentPath) : [];
    const checkHealth = useCallback(() => {
        if (!enableHealthCheck)
            return;
        const statuses = {};
        for (const w of workers)
            statuses[w] = "checking";
        setHealth(statuses);
        for (const w of workers) {
            const url = WORKER_URLS[w];
            if (!url) {
                setHealth((prev) => ({ ...prev, [w]: "unknown" }));
                continue;
            }
            const controller = new AbortController();
            const timeout = setTimeout(() => controller.abort(), 3000);
            fetch(`${url}/health`, { signal: controller.signal, mode: "cors" })
                .then((r) => {
                setHealth((prev) => ({
                    ...prev,
                    [w]: r.ok ? "ok" : "error",
                }));
            })
                .catch(() => {
                setHealth((prev) => ({ ...prev, [w]: "error" }));
            })
                .finally(() => clearTimeout(timeout));
        }
    }, [enableHealthCheck, workers.join(",")]);
    useEffect(() => {
        if (open && enableHealthCheck)
            checkHealth();
    }, [open, checkHealth]);
    // Close on outside click
    useEffect(() => {
        if (!open)
            return;
        function handleClick(e) {
            if (panelRef.current && !panelRef.current.contains(e.target)) {
                setOpen(false);
            }
        }
        document.addEventListener("mousedown", handleClick);
        return () => document.removeEventListener("mousedown", handleClick);
    }, [open]);
    // Close on Escape
    useEffect(() => {
        if (!open)
            return;
        function handleKey(e) {
            if (e.key === "Escape")
                setOpen(false);
        }
        document.addEventListener("keydown", handleKey);
        return () => document.removeEventListener("keydown", handleKey);
    }, [open]);
    // Gate after all hooks are registered (rules of hooks)
    if (!isAdmin || !entry)
        return null;
    return (_jsxs("div", { ref: panelRef, className: "fixed bottom-4 right-20 z-[60]", children: [_jsx("button", { type: "button", onClick: () => setOpen((v) => !v), className: `flex h-9 w-9 items-center justify-center rounded-lg font-mono text-sm font-bold shadow-lg transition-all ${open
                    ? "bg-slate-800 text-white"
                    : "bg-surface/80 text-muted hover:bg-surface-2 hover:text-content backdrop-blur-sm border border-line"}`, title: "Infrastructure Info", children: "{}" }), open && (_jsxs("div", { className: "absolute bottom-12 right-0 w-80 rounded-xl bg-surface shadow-2xl border border-line overflow-hidden text-sm", children: [_jsxs("div", { className: "bg-slate-800 text-white px-4 py-3", children: [_jsx("div", { className: "font-semibold", children: entry.label }), _jsx("a", { href: entry.repoUrl, target: "_blank", rel: "noopener noreferrer", className: "text-xs text-slate-300 hover:text-white transition-colors", children: entry.repo })] }), _jsxs("div", { className: "p-4 space-y-3 max-h-80 overflow-y-auto", children: [_jsxs("div", { children: [_jsx("div", { className: "text-xs font-semibold text-muted uppercase tracking-wide mb-1.5", children: "Workers" }), _jsx("div", { className: "space-y-1", children: workers.map((w) => {
                                            const url = WORKER_URLS[w];
                                            return (_jsxs("div", { className: "flex items-center gap-2 text-content", children: [enableHealthCheck && _jsx(HealthDot, { status: health[w] ?? "unknown" }), url ? (_jsx("a", { href: url, target: "_blank", rel: "noopener noreferrer", className: "hover:text-primary transition-colors truncate", children: w })) : (_jsx("span", { className: "truncate", children: w }))] }, w));
                                        }) })] }), entry.packages.length > 0 && (_jsxs("div", { children: [_jsx("div", { className: "text-xs font-semibold text-muted uppercase tracking-wide mb-1.5", children: "Packages" }), _jsx("div", { className: "flex flex-wrap gap-1.5", children: entry.packages.map((p) => (_jsxs("span", { className: "inline-block rounded-md bg-surface-2 px-2 py-0.5 text-xs text-muted", children: ["@theg1team/", p] }, p))) })] })), currentPath && (_jsx("div", { className: "border-t border-line pt-2", children: _jsxs("div", { className: "text-xs text-muted", children: ["Route: ", _jsx("span", { className: "font-mono text-muted", children: currentPath })] }) }))] })] }))] }));
}
//# sourceMappingURL=DevInfoOverlay.js.map